import { useEffect, useMemo, useState } from 'react';
import { TruncatedText } from 'components/base/TruncatedText';

import { useAppSelector, useAppDispatch } from 'redux/store';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import TableViewLayout from 'layouts/TableViewLayout';

import { getTriggersPaginated } from '../../../../redux/slices/triggersSlice';
import type { TriggerRow } from 'api/triggers/endpoints/getTriggers';
import { formatTriggerStatusList } from 'api/triggers/endpoints/types';
import { trigger } from '@norscom/nap-shared';
import { getDetails } from 'api/details/endpoints/getDetails';
import { RecordDetailsModel } from 'details';
import { getActions, ActionButton, ActionDropdown } from 'actions';
import { toTriggerEntity } from './triggerActions';
import { TriggerDetails } from './details/TriggerDetails';

import '../zones/dropdownStyle.css';

// While the details pane is open, this one column replaces the data columns: the
// pane shows the rest. `maxWidth: 0` lets the cell truncate instead of widening the
// table.
const compactColumn = {
  id: 'trigger',
  header: 'Trigger',
  enableSorting: false,
  meta: { cellProps: { style: { maxWidth: 0, width: '100%' } } },
  cell: ({ row }: { row: { original: TriggerRow } }) => (
    <TruncatedText text={row.original.zone_name ? `${row.original.name} (${row.original.zone_name})` : row.original.name}>
      <span className="fw-semibold">{row.original.name}</span>
      {row.original.zone_name && <span className="text-muted ms-2">{row.original.zone_name}</span>}
    </TruncatedText>
  ),
};

const ListTriggers = () => {
  const dispatch = useAppDispatch();

  const { triggers, config: headerConfig, filtersConfig, pagination, error, loading } = useAppSelector((state) => state.triggers);

  const userData = useAppSelector((state) => state.auth?.userData);

  const isLoading = loading?.getTriggersPaginated ?? false;

  const pageName = 'triggers';
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    void dispatch(getTriggersPaginated({}));
  }, [dispatch]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    generateColumns,
    refresh,
    isSilentRefresh,
    getFormattedData,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: getTriggersPaginated,
    columnFormats: { updated_at: 'datetime' },
    columnCells: {
      from_status: (row: TriggerRow) => formatTriggerStatusList(row.from_status),
      to_status: (row: TriggerRow) => formatTriggerStatusList(row.to_status),
    },
  });

  // The pane's record is read here, not in the pane, so that whatever refreshes the
  // list (a row's or the toolbar's action, the refresh button) re-reads it too: an
  // edit or delete from the list must not leave the pane showing the old trigger.
  // One model per selected id (see RecordDetailsModel).
  const detailsModel = useMemo(() => (selectedId === null ? null : new RecordDetailsModel(selectedId, (id: number) => getDetails(trigger, id))), [selectedId]);
  const refreshAll = () => {
    refresh();
    detailsModel?.refresh();
  };

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: { row: { original: TriggerRow } }) => {
      const actions = getActions({ entities: [toTriggerEntity(row.original)], refresh: refreshAll });
      return <ActionDropdown actions={actions} items={['edit', 'addToTransportTrigger', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  const columns = selectedId === null ? [...baseColumns, folderColumn, actionsColumn] : [compactColumn, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(triggers), [triggers]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => String(row.trigger_id),
  });

  const { currentPage, totalPages, totalItems } = pagination;

  const selectionEntities = table.getSelectedRowModel().rows.map((r: { original: TriggerRow }) => toTriggerEntity(r.original));
  const toolbarActions = getActions({ entities: selectionEntities, refresh: refreshAll });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={headerConfig.map((c) => ({
        key: c.header_key,
        label: c.label,
      }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refreshAll}
      isLoading={isLoading}
      isSilentRefresh={isSilentRefresh}
      onRowClick={(row: TriggerRow) => setSelectedId(row.trigger_id)}
      selectedRowId={selectedId === null ? undefined : String(selectedId)}
      sidePanel={detailsModel !== null && <TriggerDetails model={detailsModel} onClose={() => setSelectedId(null)} onChanged={refreshAll} />}
      toolbarButtons={
        <>
          <ActionButton actions={toolbarActions} action="createTrigger" variant="primary" />
          <ActionButton actions={toolbarActions} action="addToTransportTrigger" variant="outline-primary" className="ms-2" label="Add selected to transport" />
        </>
      }
    />
  );
};

export default ListTriggers;
